import React from 'react';
import LegalPageLayout, { LegalSection } from '../components/LegalPageLayout';

const KycAmlPolicyPage = () => {
  return (
    <LegalPageLayout
      testId="kyc-aml-policy-page"
      eyebrow="Legal"
      title="KYC / AML Policy"
      lastUpdated="February 2026"
    >
      <LegalSection number="1" title="Purpose">
        <p>
          Sebastian Alliance Group LLC ("SAG") is committed to preventing the
          use of its platforms and services for money laundering, terrorist
          financing, sanctions evasion or any other financial crime. This
          Know-Your-Customer / Anti-Money-Laundering ("KYC/AML") Policy
          summarises the controls SAG applies to onboarding, monitoring and
          reporting.
        </p>
      </LegalSection>

      <LegalSection number="2" title="Regulatory framework">
        <p>
          SAG operates from the United States and observes applicable
          requirements under: the Bank Secrecy Act (BSA), USA PATRIOT Act,
          rules published by the Financial Crimes Enforcement Network
          (FinCEN) and the Office of Foreign Assets Control (OFAC),
          Florida state law, and card-network rules (Visa, Mastercard,
          American Express, Discover). Where SAG facilitates payments in
          other jurisdictions, we cooperate with the local regulated
          processor or banking partner responsible for compliance in that
          jurisdiction.
        </p>
        <p>
          SAG is not itself a bank, money services business or money
          transmitter. Payment settlement is performed by regulated
          third-party processors (including Stripe, Inc. and Fiserv, Inc.),
          each of which applies its own KYC/AML programme.
        </p>
      </LegalSection>

      <LegalSection number="3" title="Customer identification (CIP)">
        <p>
          Before a merchant, corporate client or high-value payer is
          onboarded, SAG collects and verifies identifying information
          appropriate to the risk of the relationship. This typically
          includes:
        </p>
        <p className="font-medium text-black mt-4">For individuals:</p>
        <ul className="list-disc pl-6 space-y-2">
          <li>Full legal name, date of birth, residential address.</li>
          <li>Government-issued photo ID (passport, driver's licence or national ID).</li>
          <li>Proof of address dated within the last 90 days (utility bill, bank statement).</li>
          <li>Tax identification number where applicable (SSN, ITIN or foreign equivalent).</li>
        </ul>
        <p className="font-medium text-black mt-4">For entities:</p>
        <ul className="list-disc pl-6 space-y-2">
          <li>Certificate of incorporation, articles of organisation or equivalent.</li>
          <li>Certificate of good standing (dated within the last 6 months).</li>
          <li>Registered business address and operating address.</li>
          <li>EIN or foreign tax identification number.</li>
          <li>Ownership structure and identification of beneficial owners holding 25% or more, and one control person, in line with FinCEN's beneficial-ownership rule.</li>
          <li>Business licence(s) where required by the jurisdiction of operation.</li>
        </ul>
      </LegalSection>

      <LegalSection number="4" title="Sanctions and PEP screening">
        <p>
          All customers, beneficial owners and control persons are screened
          against consolidated sanctions lists — including but not limited
          to OFAC SDN, EU consolidated sanctions, UK OFSI, and UN Security
          Council lists — and against politically-exposed-person (PEP) and
          adverse-media data at onboarding and on an ongoing basis. A
          confirmed match will result in refusal or termination of the
          relationship and, where required by law, a report to the
          appropriate authority.
        </p>
      </LegalSection>

      <LegalSection number="5" title="Risk-based approach">
        <p>
          SAG assigns each customer a risk rating (low, medium, high) based
          on factors including: type of business, geography, expected
          transaction volume and pattern, involvement of high-risk
          jurisdictions, use of cash-intensive activity, and the presence
          of PEPs. Higher-risk relationships receive enhanced due
          diligence (EDD), including source-of-funds evidence, senior
          approval and more frequent review.
        </p>
      </LegalSection>

      <LegalSection number="6" title="Prohibited activities">
        <p>
          SAG platforms may not be used to sell or accept payment for,
          among other things: illegal narcotics; unlicensed pharmaceuticals;
          weapons, ammunition or explosives; child sexual abuse material or
          any other illegal content; human trafficking or forced labour;
          unlicensed gambling or lotteries; unregistered securities offerings
          or investment schemes; multi-level-marketing pyramid schemes;
          shell-company facilitation; goods or services sanctioned by the
          United States or an applicable jurisdiction; and any activity
          contrary to card-network rules or applicable law.
        </p>
      </LegalSection>

      <LegalSection number="7" title="Ongoing monitoring">
        <p>
          Transactions processed on SAG platforms are monitored for patterns
          consistent with money laundering, terrorist financing, fraud or
          sanctions evasion — including structuring, rapid movement of
          funds, unusual chargeback rates, and mismatches between declared
          business type and actual activity. Alerts are reviewed by
          compliance personnel and escalated where necessary.
        </p>
      </LegalSection>

      <LegalSection number="8" title="Suspicious activity and reporting">
        <p>
          SAG cooperates with regulated processors and banking partners in
          the filing of Suspicious Activity Reports (SARs) and Currency
          Transaction Reports (CTRs) where triggered. Where SAG has a
          direct obligation to report, it does so in accordance with US
          federal and Florida state law. Customers are prohibited from
          "tipping off" any person that a report has been or may be filed.
        </p>
      </LegalSection>

      <LegalSection number="9" title="Recordkeeping">
        <p>
          KYC files, transaction records, screening results, alert
          investigations and any reports made are retained for at least
          five (5) years after the end of the relationship or the date of
          the transaction, whichever is later, unless a longer period is
          required by law.
        </p>
      </LegalSection>

      <LegalSection number="10" title="Governance and training">
        <p>
          SAG maintains a written AML programme approved by senior
          management, designates a compliance officer responsible for its
          day-to-day operation, delivers periodic training to staff who
          onboard customers or handle transactions, and engages
          independent testing of the programme on a risk-appropriate
          cadence.
        </p>
      </LegalSection>

      <LegalSection number="11" title="Refusal, restriction and termination">
        <p>
          SAG reserves the right to refuse an application, restrict
          functionality, hold or reverse funds, and terminate any account
          where a KYC/AML, sanctions, fraud or acceptable-use concern
          arises, in line with our{' '}
          <a href="/terms" className="text-[#C9A227] hover:underline">
            Terms of Service
          </a>{' '}
          and applicable law.
        </p>
      </LegalSection>

      <LegalSection number="12" title="Contact">
        <p>
          To report a concern, request a copy of the information we hold
          about you, or contact the SAG compliance function, email{' '}
          <a
            href="mailto:ecommerce@sebasalliance.net"
            className="text-[#C9A227] hover:underline"
          >
            ecommerce@sebasalliance.net
          </a>
          .
        </p>
      </LegalSection>
    </LegalPageLayout>
  );
};

export default KycAmlPolicyPage;
